// "Oak city winter": dusk to night among tall evergreens, first snow → the moon rises → chimes, far windows, shake a tree → the painting.
export const oakWinter = {
  slug: "oak-city-winter",
  duration: 28,
  stages: [
    { id: "dusk", at: 0, hint: "dusk among the evergreens" },
    { id: "moon", at: 7, hint: "the moon rises" },
    { id: "cozy", at: 13, hint: "tap a tree" },
    { id: "settle", at: 21.5, hint: "" },
  ],
  line: { text: "Even in the dark, the sky keeps a little sunset.", from: 14, to: 27.5, pos: "low" },
  touch: { kind: "shake", from: 13, until: 21.5 },
  // painted panorama: evergreens under first snow, the moon rising through the branches, far windows lit
  layers: [
    { src: "/arcs/oak/winter.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, mist: 0.12, mistCol: "#c8d2ee", opacity: [[0, 1], [13, 0.78], [21.5, 0.78], [23, 1]], show: [-1, 0, 99, 99] },
  ],
  cues: [[13.4, "pop"], [15.8, "pop"], [18.6, "pop"]],
  ambient: { scene: "winter", until: 99, then: [] },
  look: { start: 0.12, lift: [7, 11], end: 0.22, yaw: [[0, -0.5], [5, 0.3], [9, 0.1], [13, 0], [28, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "winter", pos: "low" },
  speed: [[0, 1], [28, 1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
uniform vec4 uShake[6];
varying vec3 vPos;
${"__NOISE__"}
float hash(float n){ return fract(sin(n * 127.1) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
// how much snow a tree at X still holds, and how fresh its last shake is
vec2 shaken(float X){
  float keep = 1.0;
  float fresh = 0.0;
  for (int i = 0; i < 6; i++){
    vec4 s = uShake[i];
    if (s.y <= 0.0 || abs(X - s.x) > 0.5) continue;
    float k = smoothstep(s.y, s.y + 0.6, uT);
    keep = min(keep, 1.0 - 0.75 * k);
    fresh = max(fresh, k * exp(-(uT - s.y) * 0.9));
  }
  return vec2(keep, fresh);
}
// first snow: little six-armed flakes and short streaks, drifting down (never round balls)
float snowfall(vec2 p, float speed){
  vec2 q = p * vec2(5.0, 4.0) + vec2(0.3 * sin(uT * 0.4 + p.y), uT * speed);
  vec2 c = floor(q);
  vec2 f = fract(q) - 0.5;
  float h = hash2(c);
  if (h < 0.72) return 0.0;
  f += 0.25 * vec2(hash2(c + 3.1) - 0.5, hash2(c + 7.7) - 0.5);
  if (h > 0.9) return (1.0 - smoothstep(0.012, 0.03, abs(f.x + f.y * 0.25))) * (1.0 - smoothstep(0.08, 0.16, abs(f.y)));
  float a = atan(f.y, f.x) + h * 6.0;
  float arm = (1.0 - smoothstep(0.0, 0.25, abs(sin(a * 3.0)))) * (1.0 - smoothstep(0.03, 0.07, length(f)));
  return max(arm, 1.0 - smoothstep(0.008, 0.016, length(f)));
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.0 + 1.3) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float night = smoothstep(1.0, 11.0, uT);
  float moonUp = smoothstep(7.0, 12.0, uT);
  float settle = smoothstep(21.5, 26.5, uT);
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    float y = vPos.y;
    // the sky: indigo deepening, the pink of the sunset lingering low
    vec3 top = mix(vec3(0.28, 0.27, 0.52), vec3(0.06, 0.07, 0.2), night);
    vec3 low = mix(vec3(0.98, 0.62, 0.62), vec3(0.66, 0.38, 0.55), night);
    col = mix(low, top, smoothstep(0.6, 5.5, y)) + 0.025 * n;
    // the moon, pale yellow, rising
    vec2 mp = vec2((th - 0.35) * uRadius, y - mix(2.0, 5.0, moonUp));
    float md = length(mp);
    col += vec3(1.0, 0.95, 0.7) * exp(-md * 1.2) * 0.25 * moonUp;
    col = mix(col, vec3(1.0, 0.96, 0.74) - 0.06 * n2, (1.0 - smoothstep(0.42, 0.46, md)) * smoothstep(7.0, 8.5, uT));
    // far windows glowing low between the trees
    vec2 wc = vec2(X * 3.0, (y - 0.7) * 6.0);
    vec2 wi = floor(wc);
    float wh = hash2(wi + 11.0);
    float win = step(0.8, wh) * step(abs(wi.y), 0.5) * step(abs(fract(wc.x) - 0.5), 0.18) * step(abs(fract(wc.y) - 0.5), 0.22);
    col = mix(col, vec3(1.0, 0.75, 0.38) * (0.85 + 0.15 * sin(uT * 1.3 + wh * 30.0)), win * smoothstep(12.5, 15.0, uT));
    // tall evergreens, two depths: tiered, snow on every tier, lit by the moon
    for (int L = 0; L < 2; L++){
      float fl = float(L);
      float cw = mix(0.75, 1.1, fl);
      float s = X / cw + fl * 0.37;
      float id = floor(s);
      float u = (fract(s) - 0.5) * cw;
      float hh = hash(id + fl * 31.0);
      if (hh < mix(0.15, 0.3, fl)) continue;
      float H = mix(3.2, 4.6, fl) + 2.2 * hash(id * 1.7 + fl);
      float yy = y - mix(0.4, 0.0, fl);
      float tier = fract((H - yy) * mix(2.6, 2.0, fl));
      float halfW = (0.06 + 0.42 * mix(0.7, 1.0, fl) * (1.0 - yy / H)) * (0.72 + 0.28 * tier);
      float tree = step(abs(u), halfW) * step(yy, H) * step(0.25, yy);
      float trunk = step(abs(u), 0.05) * step(yy, 0.4) * step(0.0, yy);
      vec3 green = mix(vec3(0.05, 0.12, 0.12), vec3(0.08, 0.17, 0.15), n2) * mix(0.55, 1.0, fl) * mix(1.0, 0.55, night);
      vec2 sh = shaken((id + 0.5 - fl * 0.37) * cw);
      float snow = (1.0 - smoothstep(0.12, 0.3, tier)) * (0.6 + 0.4 * n2) * sh.x;
      vec3 snowC = mix(vec3(0.82, 0.8, 0.9), vec3(0.95, 0.95, 1.0), moonUp) * mix(0.85, 1.0, fl) * mix(1.0, 0.7, night * (1.0 - moonUp));
      snowC += vec3(0.25, 0.22, 0.1) * moonUp * smoothstep(-halfW, halfW, u) * 0.35;
      vec3 tc = mix(green, snowC, snow);
      col = mix(col, tc, tree);
      col = mix(col, vec3(0.1, 0.07, 0.07), trunk);
      // the soft puff of snow off a shaken tree
      float puff = sh.y * smoothstep(0.45, 0.75, snoise(vec3(u * 3.0, (yy - H * 0.6 + (uT * 0.6)) * 2.0, uT * 0.3)) * 0.5 + 0.5) * step(abs(u), halfW + 0.35) * step(yy, H + 0.3);
      col = mix(col, vec3(0.92, 0.93, 1.0), puff * 0.75 * fl);
    }
    col = mix(col, vec3(0.96, 0.97, 1.0), snowfall(vec2(X, y), 0.7) * 0.85);
    local = vec2(X, y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    col = mix(vec3(0.6, 0.62, 0.78), vec3(0.85, 0.86, 0.95), n) * mix(1.0, 0.55, night) * (0.95 + 0.08 * n2);
    col += vec3(0.25, 0.22, 0.12) * moonUp * exp(-pow(atan(xz.x, -xz.y) - 0.35, 2.0) * 4.0) * 0.25;
    col += vec3(1.0, 0.7, 0.6) * 0.05 * (1.0 - night);
    local = xz;
  } else {
    col = mix(vec3(0.24, 0.24, 0.48), vec3(0.05, 0.06, 0.18), night) + 0.02 * n;
    col = mix(col, vec3(0.96, 0.97, 1.0), snowfall(vPos.xz, 0.5) * 0.7);
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  float m = smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25);
  col = mix(col, paint, m);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
